import { useTranslation } from 'react-i18next';
import React, { memo, useMemo, useCallback } from 'react';
import { IconRcFeedMore } from '@/assets/stream';
import { isOfficalAddress, useCurrentUserId, toastError } from '@/utils';
import cx from 'clsx';
import { CommentEligibilityModal } from '@debank/content-editor';
import message from 'antd/es/message';
import { useHistory } from 'react-router-dom';

import {
  Dropdown,
  HelperTooltip,
  Tips,
  openReferralModal,
  useUnionFollow,
  Modal,
} from '@/components';
import { useUserJoinedGroupList } from '@/views/Group/hooks';
import { GROUP_MODAL_TYPE, openGroupModal } from '@/views/Group/components/GroupModalManager';
import { useSwitch } from '@/hooks';
import { useChannelStreamPin, useChannelStreamWithdraw } from '@/views/Channel/hooks/stream';

import { FeedArticleDetail, ArticleCommentEligibility } from '../../types';
import { useArticleWithdraw, useArticleEditCommentEligibility } from '../../hooks';
import { useMuteOperate, usePinOperateStream } from './hooks';
import { MuteModal, HidePostModal } from './ConfirmModal';
import { isWithinTenMinutes } from './utils';
import styles from './index.module.scss';

const DETAIL_LINK_PREFIX = `${window.location.origin}/stream`;

const SupportEditType = ['text', 'thread', 'pay_thread'];

const EditTypeMap: Record<'text' | 'thread' | 'pay_thread', string> = {
  text: 'post',
  thread: 'thread',
  pay_thread: 'thread',
};

export const FeedMoreAction = memo(
  ({
    articleDetail,
    className,
    onPermissionChanged,
    hideGroupPin,
    isInChannel,
  }: {
    articleDetail: FeedArticleDetail;
    className?: string;
    onPermissionChanged?(): void;
    hideGroupPin?: boolean;
    isInChannel?: boolean;
  }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const userId = useCurrentUserId();
    const isMe = userId === articleDetail.article.creator_id;
    const isChannelOwner = isInChannel && articleDetail.article.channel?.owner_id === userId;
    const streamDetailLink = `${DETAIL_LINK_PREFIX}/${articleDetail.article.id}`;
    const { mutate: withdrawArticle } = useArticleWithdraw(articleDetail.article.id);
    const { mutateAsync: updateCommentEligibility } = useArticleEditCommentEligibility(
      articleDetail.article.id,
    );
    const isOfficialFeed = isOfficalAddress(articleDetail.article.creator_id);
    const { data: joinedGroups } = useUserJoinedGroupList(userId);
    const joinedGroupsList = useMemo(() => joinedGroups?.groups || [], [joinedGroups?.groups]);
    const signerGroupList = useMemo(
      () => joinedGroupsList.filter(group => group.is_signer),
      [joinedGroupsList],
    );

    const groupMenuList = useMemo(() => {
      return [
        !hideGroupPin && signerGroupList.length
          ? {
              label: t('FeedMoreAction.pin-to-group'),
              children:
                signerGroupList.length > 1
                  ? signerGroupList.map(group => ({
                      label: (
                        <HelperTooltip
                          disable={group?.is_active}
                          placement="left"
                          title={t('FeedMoreAction.cannot-pin-reason')}>
                          <div className={cx(!group?.is_active && styles.disabledMenu)}>
                            {group.name}
                          </div>
                        </HelperTooltip>
                      ),

                      onClick: () => {
                        group?.is_active &&
                          openGroupModal(GROUP_MODAL_TYPE.PIN_ARTICLE, {
                            groupId: group.id,
                            id: articleDetail.article.id,
                          });
                      },
                    }))
                  : undefined,
              childrenDropDownProps: {
                placement: 'bottomLeft',
                align: { offset: [-176, -30] },
              },
              onClick: () => {
                if (signerGroupList.length > 1) {
                  return;
                }
                if (signerGroupList?.[0]?.is_active) {
                  openGroupModal(GROUP_MODAL_TYPE.PIN_ARTICLE, {
                    groupId: signerGroupList?.[0]?.id,
                    id: articleDetail.article.id,
                  });
                }
              },
            }
          : undefined,
        // joinedGroupsList.length
        //   ? {
        //       label: 'Share to group',
        //       children: joinedGroupsList.map(group => ({
        //         label: group.name,
        //         onClick: () => {
        //           repostStreamPostToGroup(group.id);
        //         },
        //       })),
        //     }
        //   : undefined,
      ];
    }, [articleDetail.article.id, hideGroupPin, signerGroupList, t]);

    const {
      on: hidePostModalVisible,
      turnOn: turnOnHidePostModal,
      turnOff: turnOffHidePostModal,
    } = useSwitch();
    const {
      on: muteModalVisible,
      turnOn: turnOnMuteModal,
      turnOff: turnOffMuteModal,
    } = useSwitch();
    // const {
    //   on: blockModalVisible,
    //   turnOn: turnOnBlockModal,
    //   turnOff: turnOffBlockModal,
    // } = useSwitch();
    const {
      on: commentEligiVisible,
      turnOn: turnOnCommentEligi,
      turnOff: turnOffCommentEligi,
    } = useSwitch();

    const isMuted = isOfficialFeed
      ? (articleDetail?.article?.creator as OfficialAccount)?.is_muted
      : (articleDetail?.article?.creator as GeneralAccount)?.desc?.user?.is_muted;
    // const isBlocked = (articleDetail?.article?.creator as GeneralAccount)?.desc?.is_block;
    const isPinned = articleDetail?.article?.is_pinned;
    const isFollowing = articleDetail?.article?.creator?.is_following;
    const isShowEdit = useMemo(
      () => SupportEditType?.includes(articleDetail?.article?.type),
      [articleDetail?.article?.type],
    );
    const isPostCanEdit = useMemo(
      () => isWithinTenMinutes(articleDetail?.article?.create_at * 1000),
      [articleDetail?.article?.create_at],
    );

    const { followMutate } = useUnionFollow({
      address: articleDetail?.article.creator_id,
      isFollowing,
    });

    const { mutate: pinOperate } = usePinOperateStream({
      action: isPinned ? 'unpin' : 'pin',
      articleId: articleDetail?.article?.id,
    });

    const { mutate: muteOperate } = useMuteOperate({
      action: isMuted ? 'unmute' : 'mute',
      userId: articleDetail?.article?.creator_id,
    });

    const editCommentEligi = useCallback(() => {
      turnOnCommentEligi();
    }, [turnOnCommentEligi]);

    const copyStreamClick = useCallback(() => {
      navigator.clipboard.writeText(streamDetailLink);
      message.success('Copied');
    }, [streamDetailLink]);

    const referStreamClick = useCallback(() => {
      openReferralModal({
        link: streamDetailLink,
        isQuest: articleDetail?.article.type === 'quest',
      });
    }, [streamDetailLink, articleDetail?.article.type]);

    const handleEditPost = useCallback(() => {
      if (!isPostCanEdit) {
        return;
      }
      const type = articleDetail?.article?.type;
      if (SupportEditType?.includes(type)) {
        // @ts-ignore
        history.push(`/edit-stream?tab=${EditTypeMap[type]}`, {
          editPostId: articleDetail?.article?.id,
        });
      }
    }, [articleDetail?.article?.id, articleDetail?.article?.type, history, isPostCanEdit]);

    const { mutate: pinInChannel } = useChannelStreamPin(
      articleDetail.article.id,
      articleDetail.article.is_channel_pinned,
      articleDetail.article.channel?.id,
    );
    const { mutate: withdrawInChannel } = useChannelStreamWithdraw(
      articleDetail.article.id,
      articleDetail.article.channel?.id,
    );

    const handleWithdrawInChannelClick = useCallback(async () => {
      await Modal.confirm({
        title: t('FeedMoreAction.138zn59'),
        content: t('FeedMoreAction.w09uiz'),
      });

      withdrawInChannel();
    }, [withdrawInChannel, t]);

    const channelMenuList = useMemo(
      () =>
        isChannelOwner
          ? [
              {
                label: (
                  <span className={styles.editBtn}>
                    {articleDetail.article.is_channel_pinned
                      ? t('HiOperateMenu.1cr4n0')
                      : t('HiOperateMenu.1px1')}
                    <Tips placement="right" title={t('FeedMoreAction.19jh10i')} />
                  </span>
                ),
                onClick: () => pinInChannel(),
              },
              {
                label: (
                  <span className={styles.editBtn}>
                    {t('FeedMoreAction.1c7du')}
                    <Tips placement="right" title={t('FeedMoreAction.w09uiz')} />
                  </span>
                ),
                onClick: () => handleWithdrawInChannelClick(),
              },
            ]
          : [],
      [
        articleDetail.article.is_channel_pinned,
        isChannelOwner,
        pinInChannel,
        handleWithdrawInChannelClick,
        t,
      ],
    );

    const commonMenus = useMemo(() => groupMenuList, [groupMenuList]);

    const commonBottomMenus = useMemo(
      () => [
        {
          label: t('FeedMoreAction.1l27745'),
          onClick: copyStreamClick,
        },
        {
          label: t('FeedMoreAction.2ezzdn'),
          onClick: referStreamClick,
        },
      ],
      [t, copyStreamClick, referStreamClick],
    );

    const myMenus = useMemo(
      () => [
        ...channelMenuList,
        isShowEdit && {
          label: (
            <span className={cx(styles.editBtn, !isPostCanEdit && styles.disabled)}>
              {t('FeedMoreAction.editPost')}{' '}
              {!isPostCanEdit ? <Tips title={t('FeedMoreAction.editPostTip')} /> : null}
            </span>
          ),
          onClick: () => handleEditPost(),
        },
        {
          label: t('FeedMoreAction.fqk85y'),
          onClick: () => editCommentEligi(),
        },
        isInChannel
          ? undefined
          : {
              label: isPinned
                ? t('FeedMoreAction.unpin-post')
                : t('FeedMoreAction.pin-to-your-profile'),
              onClick: () => pinOperate(),
            },
        isChannelOwner
          ? undefined
          : {
              label: t('FeedMoreAction.1aanw66'),
              render: (label: string) => {
                const disableTip =
                  articleDetail?.article?.type === 'pay_qa' && articleDetail.article.thread_count
                    ? t('FeedMoreAction.replyieHidden')
                    : articleDetail?.article?.pay_count
                      ? t('FeedMoreAction.hideSupprt')
                      : null;

                return disableTip ? (
                  <div className={styles.disabledMenu}>
                    {label}
                    <Tips title={disableTip} className={styles.disabledMenu} placement="right" />
                  </div>
                ) : (
                  <div onClick={turnOnHidePostModal}>{label}</div>
                );
              },
            },
        ...commonMenus,
        ...commonBottomMenus,
      ],
      [
        isShowEdit,
        isPostCanEdit,
        t,
        isPinned,
        commonMenus,
        commonBottomMenus,
        handleEditPost,
        editCommentEligi,
        pinOperate,
        articleDetail.article?.type,
        articleDetail.article.thread_count,
        articleDetail.article?.pay_count,
        turnOnHidePostModal,
        channelMenuList,
        isChannelOwner,
        isInChannel,
      ],
    );

    const othersMenus = useMemo(
      () => [
        ...channelMenuList,
        {
          label: `${isMuted ? t('FeedMoreAction.unmute') : t('FeedMoreAction.mute')} ${
            articleDetail?.article?._author
          }`,
          onClick: () => {
            if (!isMuted) {
              turnOnMuteModal();
            } else {
              muteOperate();
            }
          },
        },
        // !isOfficialFeed
        //   ? {
        //       label: `${isBlocked ? t('FeedMoreAction.unblock') : t('FeedMoreAction.block')}  ${
        //         articleDetail?.article?._author
        //       }`,
        //       onClick: () => turnOnBlockModal(),
        //     }
        //   : undefined,
        ...commonMenus,
        {
          label: isFollowing ? t('FollowButton.1trw9m2') : t('FollowButton.follow'),
          onClick: () => followMutate(),
        },
        ...commonBottomMenus,
      ],
      [
        isMuted,
        t,
        articleDetail?.article?._author,
        commonMenus,
        turnOnMuteModal,
        muteOperate,
        isFollowing,
        followMutate,
        commonBottomMenus,
        channelMenuList,
      ],
    );

    const menu = useMemo(
      () =>
        (isMe ? myMenus : othersMenus).filter(i => !!i) as {
          label: string;
          onClick?: () => void;
        }[],
      [isMe, myMenus, othersMenus],
    );

    const handleCommentEligi = useCallback(
      async (data: ArticleCommentEligibility) => {
        try {
          await updateCommentEligibility(data);
          onPermissionChanged?.();
        } catch (error) {
          toastError((error as string) || t('components_Row.yoexct'));
        }
      },
      [updateCommentEligibility, onPermissionChanged, t],
    );

    const handleMoreClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    }, []);

    const handleMuteModalConfirm = useCallback(() => muteOperate(), [muteOperate]);

    const handleClickDropdown = useCallback(e => {
      e.stopPropagation();
    }, []);

    return userId && menu ? (
      <>
        <div onClick={handleClickDropdown}>
          <Dropdown
            overlayClassName={styles.menuDropdown}
            data={menu as any}
            menuType="cascade"
            placement="bottomRight">
            <div onClick={handleMoreClick} className={cx(styles.feedMoreOp, className)}>
              <IconRcFeedMore />
            </div>
          </Dropdown>
        </div>
        <MuteModal
          on={muteModalVisible}
          turnOff={turnOffMuteModal}
          author={articleDetail?.article?._author}
          onConfirm={handleMuteModalConfirm}
        />
        <HidePostModal
          on={hidePostModalVisible}
          turnOff={turnOffHidePostModal}
          onConfirm={withdrawArticle}
        />
        {/* <BlockModal
          on={blockModalVisible}
          turnOff={turnOffBlockModal}
          isBlocked={isBlocked}
          userId={articleDetail?.article.creator_id}
          userName={articleDetail?.article?._author}
        /> */}
        {commentEligiVisible ? (
          <CommentEligibilityModal
            on={commentEligiVisible}
            turnOff={turnOffCommentEligi}
            onSubmit={handleCommentEligi}
            initEligibity={articleDetail.article.permissions}
          />
        ) : null}
      </>
    ) : null;
  },
);
